/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { call, delay, put, race } from "redux-saga/effects";

import * as applicationApi from "Services/ApplicationApi";

import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { FilterValueFieldNames } from "Constants/FilterFields";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import {
  IApplicationFormActivitiesEventsRequest,
  IUserprofileActivitiesEventsRequest,
} from "Interfaces/Application/IActivitiesEventsRequest";
import { IApplicationDetailsDataResult } from "Interfaces/Application/IApplicationDetailsDataResult";
import { IApplicationFlagResponse } from "Interfaces/Application/IApplicationFlagResponse";
import { IApplicationFormActivityEvent } from "Interfaces/Application/IApplicationFormActivityEvent";
import { IApplicationFormActivityEventList } from "Interfaces/Application/IApplicationFormActivityEventList";
import { IApplicationQuestionResponse } from "Interfaces/Application/IApplicationQuestionResponse";
import { IApplicationUserProfileInformation } from "Interfaces/Application/IApplicationUserProfileInformation";
import { IApiErrorResult, IApiResult2 } from "Utility/IApiResult";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import {
  fetchApplicationFlagsBySelectedApplicationIdFail,
  fetchApplicationFlagsBySelectedApplicationIdSuccess,
  getActivityEventByIdFail,
  getActivityEventByIdSuccess,
  getAdditionalActivitiesEventsBySelectedUserProfileIdFail,
  getAdditionalActivitiesEventsBySelectedUserProfileIdSuccess,
  getApplicationQuestionsBySelectedApplicationIdFail,
  getApplicationQuestionsBySelectedApplicationIdSuccess,
  getHighlightedActivitiesEventsBySelectedApplicationIdFail,
  getHighlightedActivitiesEventsBySelectedApplicationIdSuccess,
  getUserProfileBySelectedApplicationIdFail,
  getUserProfileBySelectedApplicationIdSuccess,
  updateApplicationStatusesAndRemoveFlagsFail,
  updateApplicationStatusesAndRemoveFlagsSuccess,
  updateApplicationTechnologyAreaAndResetScoresFail,
  updateApplicationTechnologyAreaAndResetScoresSuccess,
  IGetActivityEvent,
  IGetAdditionalActivitiesEventsBySelectedUserprofileId,
  IGetApplicationFlagsBySelectedApplicationId,
  IGetApplicationQuestionsBySelectedApplicationId,
  IGetHighlightedActivitiesEventsBySelectedApplicationId,
  IGetUserProfileBySelectedApplicationId,
  IUpdateApplicationStatusesAndRemoveFlags,
  IUpdateApplicationTechnologyAreaAndResetScores,
} from "./ApplicationDetailsAction";

/**
 * Fetches the user profile information by application id
 * @param {IGetUserProfileBySelectedApplicationId} action for fetching an application by id
 * @returns {void}
 */
function* handleUserProfileFetchByApplicationId(action: IGetUserProfileBySelectedApplicationId) {
  const failedMessage = "Failed to fetch the selected userprofile info by application id.";
  const { selectedApplicationId, tenant } = action;

  try {
    const {
      result,
      timeout,
    }: IApiResult2<IApplicationDetailsDataResult<IApplicationUserProfileInformation>> | any =
      yield race({
        result: call(
          applicationApi.getProfileInformationByApplicationId,
          selectedApplicationId,
          tenant
        ),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      });

    if (timeout) {
      yield put(getUserProfileBySelectedApplicationIdFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }
    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          getUserProfileBySelectedApplicationIdSuccess(
            result.parsedBody as IApplicationUserProfileInformation
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          getUserProfileBySelectedApplicationIdFail(
            (result.parsedBody as IApiErrorResult).error.message
          )
        );
        break;
      default:
        yield put(getUserProfileBySelectedApplicationIdFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(getUserProfileBySelectedApplicationIdFail(failedMessage));
  }
}

/**
 * Fetches the application flags by application id
 * @param {IGetApplicationFlagsBySelectedApplicationId} action for fetching application flags by application form id
 * @returns {void}
 */
function* handleFetchApplicationFlagsByApplicationId(
  action: IGetApplicationFlagsBySelectedApplicationId
) {
  const failedMessage = "Failed to fetch the selected application flags by application id.";
  const { selectedApplicationId } = action;

  try {
    const {
      result,
      timeout,
    }: IApiResult2<IApplicationDetailsDataResult<IApplicationFlagResponse[]>> | any = yield race({
      result: call(applicationApi.getApplicationFlagsByApplicationId, selectedApplicationId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchApplicationFlagsBySelectedApplicationIdFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          fetchApplicationFlagsBySelectedApplicationIdSuccess(
            result.parsedBody as IApplicationFlagResponse[]
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          fetchApplicationFlagsBySelectedApplicationIdFail(
            (result.parsedBody as IApiErrorResult).error.message
          )
        );
        break;
      default:
        yield put(fetchApplicationFlagsBySelectedApplicationIdFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(fetchApplicationFlagsBySelectedApplicationIdFail(failedMessage));
  }
}

/**
 * Fetches the application questions by application id
 * @param {IGetApplicationQuestionsBySelectedApplicationId} action for fetching an application by id
 * @returns {void}
 */
function* handleApplicationQuestionsFetchByApplicationId(
  action: IGetApplicationQuestionsBySelectedApplicationId
) {
  const failedMessage = "Failed to fetch the selected application questions by application id.";
  const { selectedApplicationId, tenant } = action;

  try {
    const {
      result,
      timeout,
    }: IApiResult2<IApplicationDetailsDataResult<IApplicationQuestionResponse[]>> | any =
      yield race({
        result: call(
          applicationApi.getApplicationQuestionsByApplicationId,
          selectedApplicationId,
          tenant
        ),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      });

    if (timeout) {
      yield put(getApplicationQuestionsBySelectedApplicationIdFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          getApplicationQuestionsBySelectedApplicationIdSuccess(
            result.parsedBody as IApplicationQuestionResponse[]
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          getApplicationQuestionsBySelectedApplicationIdFail(
            (result.parsedBody as IApiErrorResult).error.message
          )
        );
        break;
      default:
        yield put(getApplicationQuestionsBySelectedApplicationIdFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(getApplicationQuestionsBySelectedApplicationIdFail(failedMessage));
  }
}

/**
 * Fetches the highlighted activities or events by application id
 * @param {IGetHighlightedActivitiesEventsBySelectedApplicationId} action for fetching activities or events by application id
 * @returns {void}
 */
function* handleHighlightedActivitiesEventsFetchByApplicationId(
  action: IGetHighlightedActivitiesEventsBySelectedApplicationId
) {
  const failedMessage = "Failed to fetch the highlighted activities or events by application id.";
  const {
    applicationId,
    pageIndex,
    pageSize,
    selectedFilterValues,
    tenant,
    sortFieldName,
    isDescendingSort,
  } = action;
  const requestBody: IApplicationFormActivitiesEventsRequest = {
    applicationFormId: applicationId,
    pageIndex,
    pageSize,
    contributionTypes: [],
    awardCategories: [],
    technologyAreas: [],
    activityOrEventTypes: [],
    tenant,
    sortFieldName,
    isDescendingSort,
  };

  selectedFilterValues.forEach((item) => {
    switch (item.fieldName) {
      case FilterValueFieldNames.ACTIVITY_OR_EVENT:
        requestBody.contributionTypes.push(item.value);
        break;
      case FilterValueFieldNames.TECHNOLOGY_AREA_GROUP:
        requestBody.awardCategories.push(item.value);
        break;
      case FilterValueFieldNames.TECHNOLOGY_AREA:
        requestBody.technologyAreas.push(item.value);
        break;
      case FilterValueFieldNames.ACTIVITY_OR_EVENT_TYPE:
        requestBody.activityOrEventTypes.push(item.value);
        break;
      default:
        return;
    }
  });

  try {
    const {
      result,
      timeout,
    }: IApiResult2<IApplicationDetailsDataResult<IApplicationFormActivityEventList>> | any =
      yield race({
        result: call(applicationApi.getActivitiesEventsByApplicationId, requestBody, tenant),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      });

    if (timeout) {
      yield put(
        getHighlightedActivitiesEventsBySelectedApplicationIdFail(GENERIC_API_TIMEOUT_ERROR)
      );
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          getHighlightedActivitiesEventsBySelectedApplicationIdSuccess(
            result.parsedBody as IApplicationFormActivityEventList
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          getHighlightedActivitiesEventsBySelectedApplicationIdFail(
            (result.parsedBody as IApiErrorResult).error.message
          )
        );
        break;
      default:
        yield put(getHighlightedActivitiesEventsBySelectedApplicationIdFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(getHighlightedActivitiesEventsBySelectedApplicationIdFail(failedMessage));
  }
}

/**
 * Fetches the additional activities or events by userprofile id
 * @param {IGetAdditionalActivitiesEventsBySelectedUserprofileId} action for fetching activities or events by userprofile id
 * @returns {void}
 */
function* handleAdditionalActivitiesEventsFetchByUserprofileId(
  action: IGetAdditionalActivitiesEventsBySelectedUserprofileId
) {
  const failedMessage = "Failed to fetch the additional activities or events by userprofile id.";
  const {
    userprofileId,
    pageIndex,
    pageSize,
    selectedFilterValues,
    tenant,
    sortFieldName,
    isDescendingSort,
  } = action;
  const requestBody: IUserprofileActivitiesEventsRequest = {
    userprofileId,
    pageIndex,
    pageSize,
    contributionTypes: [],
    awardCategories: [],
    technologyAreas: [],
    activityOrEventTypes: [],
    tenant,
    sortFieldName,
    isDescendingSort,
  };

  selectedFilterValues.forEach((item) => {
    switch (item.fieldName) {
      case FilterValueFieldNames.ACTIVITY_OR_EVENT:
        requestBody.contributionTypes.push(item.value);
        break;
      case FilterValueFieldNames.TECHNOLOGY_AREA_GROUP:
        requestBody.awardCategories.push(item.value);
        break;
      case FilterValueFieldNames.TECHNOLOGY_AREA:
        requestBody.technologyAreas.push(item.value);
        break;
      case FilterValueFieldNames.ACTIVITY_OR_EVENT_TYPE:
        requestBody.activityOrEventTypes.push(item.value);
        break;
      default:
        return;
    }
  });

  try {
    const {
      result,
      timeout,
    }: IApiResult2<IApplicationDetailsDataResult<IApplicationFormActivityEventList>> | any =
      yield race({
        result: call(applicationApi.getActivitiesEventsByUserprofileId, requestBody, tenant),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      });

    if (timeout) {
      yield put(
        getAdditionalActivitiesEventsBySelectedUserProfileIdFail(GENERIC_API_TIMEOUT_ERROR)
      );
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          getAdditionalActivitiesEventsBySelectedUserProfileIdSuccess(
            result.parsedBody as IApplicationFormActivityEventList
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          getAdditionalActivitiesEventsBySelectedUserProfileIdFail(
            (result.parsedBody as IApiErrorResult).error.message
          )
        );
        break;
      default:
        yield put(getAdditionalActivitiesEventsBySelectedUserProfileIdFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(getAdditionalActivitiesEventsBySelectedUserProfileIdFail(failedMessage));
  }
}

function* handleActivityEventFetchById(action: IGetActivityEvent) {
  const failedMessage = "Failed to fetch the selected activity or event id.";
  const { activityEventId, contributionType } = action;

  try {
    const {
      result,
      timeout,
    }: IApiResult2<IApplicationDetailsDataResult<IApplicationFormActivityEvent>> | any = yield race(
      {
        result: call(applicationApi.getActivityEventById, { activityEventId, contributionType }),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      }
    );

    if (timeout) {
      yield put(getActivityEventByIdFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(getActivityEventByIdSuccess(result.parsedBody as IApplicationFormActivityEvent));
        break;
      case HttpStatusCode.BadRequest:
        yield put(getActivityEventByIdFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(getActivityEventByIdFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(getActivityEventByIdFail(failedMessage));
  }
}

function* handleUpdateApplicationStatusesAndRemoveFlags(
  action: IUpdateApplicationStatusesAndRemoveFlags
) {
  const failedMessage = "Failed to update the application status and/or remove flags.";
  const { applicationFormId, technologyFocusAreaStatuses, removeFlags, justification } = action;

  try {
    const {
      result,
      timeout,
    }: IApiResult2<IApplicationDetailsDataResult<IApplicationFormActivityEvent>> | any = yield race(
      {
        result: call(applicationApi.manageApplication, {
          applicationFormId,
          technologyFocusAreaStatuses,
          removeFlags,
          justification,
        }),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      }
    );

    if (timeout) {
      yield put(updateApplicationStatusesAndRemoveFlagsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(updateApplicationStatusesAndRemoveFlagsSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          updateApplicationStatusesAndRemoveFlagsFail(
            (result.parsedBody as IApiErrorResult).error.message
          )
        );
        break;
      default:
        yield put(updateApplicationStatusesAndRemoveFlagsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(updateApplicationStatusesAndRemoveFlagsFail(failedMessage));
  }
}

function* handleUpdateApplicationTechnologyAreaAndResetScores(
  action: IUpdateApplicationTechnologyAreaAndResetScores
) {
  const failedMessage = "Failed to update the application technology area and/or reset scores.";
  const { applicationFormId, technologyFocusAreas, justification } = action;

  try {
    const {
      result,
      timeout,
    }: IApiResult2<IApplicationDetailsDataResult<IApplicationFormActivityEvent>> | any = yield race(
      {
        result: call(applicationApi.updateApplicationTechnologyArea, {
          applicationFormId,
          applicationFormTechnologyFocusAreas: technologyFocusAreas,
          justification,
        }),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      }
    );

    if (timeout) {
      yield put(updateApplicationTechnologyAreaAndResetScoresFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(updateApplicationTechnologyAreaAndResetScoresSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          updateApplicationTechnologyAreaAndResetScoresFail(
            (result.parsedBody as IApiErrorResult).error.message
          )
        );
        break;
      default:
        yield put(updateApplicationTechnologyAreaAndResetScoresFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(updateApplicationTechnologyAreaAndResetScoresFail(failedMessage));
  }
}

export {
  handleActivityEventFetchById,
  handleFetchApplicationFlagsByApplicationId,
  handleUserProfileFetchByApplicationId,
  handleApplicationQuestionsFetchByApplicationId,
  handleAdditionalActivitiesEventsFetchByUserprofileId,
  handleHighlightedActivitiesEventsFetchByApplicationId,
  handleUpdateApplicationStatusesAndRemoveFlags,
  handleUpdateApplicationTechnologyAreaAndResetScores,
};
